/**
 * PURPOSE: Cliente de elegibles Rugby (FTR-0015) para Winscore.
 * RESPONSIBILITIES: Estado de flags (sin datos); Administración General → Elegibles Rugby (evento-deportes, panorama,
 *   regla con confirmación explícita, plazo/cambios, override por partido y por fecha del fixture —fijar o volver al
 *   plazo relativo—, extensión por institución, cambios extra, historial, lado del partido y edición administrativa)
 *   con la cabecera X-Hayllis-Company del interceptor de FTR-0012; y para la planilla: estado de envíos y "Iniciar
 *   partido". Devuelve el sobre V2 desenvuelto y propaga los errores HTTP al llamador.
 * COLLABORATORS: httpService.js, CompanyRugbyLineupsTab.jsx, RugbyLineupSidePanel.jsx, useRugbyLineupsStatus.js,
 *   planilla Rugby (useRugbyDelegateStatus.js, RugbyStartMatchPanel.jsx).
 * ROUTE: winscore/src/services/rugbyLineupsService.js
 */
import httpService from "./httpService.js";

export const RUGBY_LINEUPS_ADMIN_BASE = "/api/hayllis/v2/admin/rugby-lineups";
export const RUGBY_LINEUPS_STAFF_BASE = "/api/hayllis/v2/rugby-lineups";

const unwrap = (response) => response?.data?.data ?? {};
const id = (value) => encodeURIComponent(String(value));
const sidePath = (idmatch, idinstitution, suffix = "") =>
  `${RUGBY_LINEUPS_ADMIN_BASE}/matches/${id(idmatch)}/institutions/${id(idinstitution)}${suffix}`;
const eventSportPath = (idevent, idsport, suffix = "") => `${RUGBY_LINEUPS_ADMIN_BASE}/events/${id(idevent)}/sports/${id(idsport)}${suffix}`;

const OFF = Object.freeze({ submissionsEnabled: false, visibilityMode: "off", startActionEnabled: false });

/** Flags del backend; ante cualquier error se asume apagado (la planilla queda como hoy). */
export const getRugbyLineupsStatus = async () => {
  try {
    const data = unwrap(await httpService.get(`${RUGBY_LINEUPS_STAFF_BASE}/status`));
    return {
      submissionsEnabled: data.submissionsEnabled === true,
      visibilityMode: ["off", "report", "enforce"].includes(data.visibilityMode) ? data.visibilityMode : "off",
      startActionEnabled: data.startActionEnabled === true,
    };
  } catch (error) {
    return { ...OFF };
  }
};

// ------------------------------ Administración General → Elegibles Rugby ------------------------------
export const listRugbyEventSports = async () => {
  const { eventSports } = unwrap(await httpService.get(`${RUGBY_LINEUPS_ADMIN_BASE}/event-sports`));
  return Array.isArray(eventSports) ? eventSports : [];
};

export const getRugbyEventSportOverview = async ({ idevent, idsport }) => unwrap(await httpService.get(eventSportPath(idevent, idsport)));

export const saveRugbyLineupRule = async ({ idevent, idsport }, payload) =>
  unwrap(await httpService.put(eventSportPath(idevent, idsport, "/rule"), payload));

export const saveRugbyLineupPolicy = async ({ idevent, idsport }, payload) =>
  unwrap(await httpService.put(eventSportPath(idevent, idsport, "/policy"), payload));

export const bulkRugbyMatchDeadlines = async ({ idevent, idsport }, payload) =>
  unwrap(await httpService.post(eventSportPath(idevent, idsport, "/deadlines/bulk"), payload));

/** Vuelve una fecha del fixture (o fecha calendario) al plazo relativo del deporte: retira sus overrides propios. */
export const clearRugbyFixtureDeadlines = async ({ idevent, idsport }, payload) =>
  unwrap(await httpService.delete(eventSportPath(idevent, idsport, "/deadlines/bulk"), { data: payload }));

export const saveRugbyMatchDeadline = async (idmatch, payload) =>
  unwrap(await httpService.put(`${RUGBY_LINEUPS_ADMIN_BASE}/matches/${id(idmatch)}/deadline`, payload));

export const retireRugbyMatchDeadline = async (idmatch, reason = "") =>
  unwrap(await httpService.delete(`${RUGBY_LINEUPS_ADMIN_BASE}/matches/${id(idmatch)}/deadline`, { data: reason ? { reason } : {} }));

export const saveRugbyInstitutionExtension = async (idmatch, idinstitution, payload) =>
  unwrap(await httpService.put(sidePath(idmatch, idinstitution, "/extension"), payload));

export const retireRugbyInstitutionExtension = async (idmatch, idinstitution, reason = "") =>
  unwrap(await httpService.delete(sidePath(idmatch, idinstitution, "/extension"), { data: reason ? { reason } : {} }));

export const grantRugbyExtraChanges = async (idmatch, idinstitution, payload) =>
  unwrap(await httpService.post(sidePath(idmatch, idinstitution, "/extra-changes"), payload));

export const getRugbySideHistory = async (idmatch, idinstitution) => unwrap(await httpService.get(sidePath(idmatch, idinstitution, "/history")));

export const getRugbySideState = async (idmatch, idinstitution) => unwrap(await httpService.get(sidePath(idmatch, idinstitution)));

export const confirmRugbySideAsAdmin = async (idmatch, idinstitution, payload) =>
  unwrap(await httpService.post(sidePath(idmatch, idinstitution, "/confirm"), payload));

// ------------------------------------------------ Planilla ------------------------------------------------
export const getRugbyPlanillaLineupStatus = async (idmatch) =>
  unwrap(await httpService.get(`${RUGBY_LINEUPS_STAFF_BASE}/matches/${id(idmatch)}/planilla-status`));

export const startRugbyMatch = async (idmatch) =>
  unwrap(await httpService.post(`${RUGBY_LINEUPS_STAFF_BASE}/matches/${id(idmatch)}/start`, {}));
